import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Handles scroll restoration + focus management on every route change.
 * Fixes beta feedback issue where new pages opened mid-scroll or at bottom.
 */
export const RouteChangeHandler = () => {
  const location = useLocation();
  const previousPathRef = useRef<string>('');

  // Disable the browser's automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const original = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = original;
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath === previousPathRef.current) {
      return;
    }
    previousPathRef.current = currentPath;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const focusTimeout = window.requestAnimationFrame(() => {
      focusPrimaryHeading();
    });

    return () => {
      window.cancelAnimationFrame(focusTimeout);
    };
  }, [location.pathname, location.search]);

  return null;
};

const focusPrimaryHeading = () => {
  const selectors = ['[data-route-heading]', 'main h1', 'h1'];
  const heading = selectors
    .map(selector => document.querySelector<HTMLElement>(selector))
    .find(Boolean);

  if (!heading) return;

  const hadTabIndex = heading.hasAttribute('tabindex');
  if (!hadTabIndex) {
    heading.setAttribute('tabindex', '-1');
  }

  heading.focus({ preventScroll: true });

  if (!hadTabIndex) {
    heading.removeAttribute('tabindex');
  }
};

export default RouteChangeHandler;
